import { storage, db, auth } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, query, getDocs, deleteDoc, doc } from 'firebase/firestore';

export interface ImageMetadata {
  id: string;
  userId: string;
  fileName: string;
  originalUrl: string;
  processedUrl?: string;
  createdAt: Date;
  isProcessed: boolean;
}

/**
 * Upload original image to Firebase Storage and save metadata to Firestore
 */
export async function uploadOriginalImage(file: File): Promise<ImageMetadata> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be logged in to upload images');
  }

  try {
    // Create storage path: bgusers/{userId}/images/original/{timestamp}-{filename}
    const timestamp = Date.now();
    const fileName = `${timestamp}-${file.name}`;
    const storagePath = `bgusers/${currentUser.uid}/images/original/${fileName}`;
    const fileRef = ref(storage, storagePath);

    console.log('[v0] Uploading original image to:', storagePath);

    // Upload to Firebase Storage
    const snapshot = await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Save metadata to Firestore
    const imageMetadata: Omit<ImageMetadata, 'id'> = {
      userId: currentUser.uid,
      fileName: file.name,
      originalUrl: downloadURL,
      createdAt: new Date(),
      isProcessed: false,
    };

    const docRef = await addDoc(
      collection(db, 'bgusers', currentUser.uid, 'images'),
      imageMetadata
    );

    console.log('[v0] Image metadata saved to Firestore with ID:', docRef.id);

    return {
      id: docRef.id,
      ...imageMetadata,
    };
  } catch (error) {
    console.error('[v0] Error uploading original image:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to upload image'
    );
  }
}

/**
 * Upload processed image to Firebase Storage and update metadata in Firestore
 */
export async function uploadProcessedImage(
  processedFile: File,
  imageId: string
): Promise<string> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be logged in to upload images');
  }

  try {
    // Get fresh auth token
    const token = await currentUser.getIdToken(true);
    console.log('[v0] User authenticated for upload. UID:', currentUser.uid);

    // Create storage path: bgusers/{userId}/images/processed/{timestamp}-{filename}
    const timestamp = Date.now();
    const fileName = `${timestamp}-${processedFile.name}`;
    const storagePath = `bgusers/${currentUser.uid}/images/processed/${fileName}`;
    const fileRef = ref(storage, storagePath);

    console.log('[v0] Uploading processed image to:', storagePath);
    console.log('[v0] Storage path includes UID:', currentUser.uid);

    // Upload to Firebase Storage
    const snapshot = await uploadBytes(fileRef, processedFile);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log('[v0] Image successfully uploaded to Storage');
    console.log('[v0] Download URL:', downloadURL);

    // Save metadata to Firestore (CRITICAL - must succeed)
    const firestoreResult = await addDoc(collection(db, 'bgusers', currentUser.uid, 'images'), {
      fileName: processedFile.name,
      processedUrl: downloadURL,
      originalUrl: downloadURL,
      isProcessed: true,
      userId: currentUser.uid,
      createdAt: new Date(),
    });

    console.log('[v0] Image metadata saved to Firestore with doc ID:', firestoreResult.id);
    return downloadURL;
  } catch (error) {
    console.error('[v0] Error uploading processed image:', error);
    if (error instanceof Error && error.message.includes('auth/invalid-api-key')) {
      throw new Error('Firebase authentication failed. Please check your API key.');
    }
    throw new Error(
      error instanceof Error ? error.message : 'Failed to upload processed image'
    );
  }
}

/**
 * Get all images for the current user
 */
export async function getUserImages(): Promise<ImageMetadata[]> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be logged in to fetch images');
  }

  try {
    // Query directly from the user's images collection without filtering by userId
    // since the collection is already scoped to the user
    const q = query(
      collection(db, 'bgusers', currentUser.uid, 'images')
    );

    const querySnapshot = await getDocs(q);
    const images: ImageMetadata[] = [];

    querySnapshot.forEach((doc: any) => {
      const data = doc.data();
      images.push({
        id: doc.id,
        userId: data.userId || currentUser.uid,
        fileName: data.fileName || 'Unknown',
        originalUrl: data.originalUrl || data.processedUrl || '',
        processedUrl: data.processedUrl,
        isProcessed: data.isProcessed || false,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
      });
    });

    console.log('[v0] Fetched', images.length, 'images for user');
    return images.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error('[v0] Error fetching user images:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to fetch images'
    );
  }
}

/**
 * Delete image from Firebase Storage and Firestore
 */
export async function deleteImage(imageId: string): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be logged in to delete images');
  }

  try {
    // Delete from Firestore
    const imageDocRef = doc(
      db,
      'bgusers',
      currentUser.uid,
      'images',
      imageId
    );
    await deleteDoc(imageDocRef);

    console.log('[v0] Deleted image from Firestore:', imageId);
  } catch (error) {
    console.error('[v0] Error deleting image:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to delete image'
    );
  }
}
